import React, { useState } from 'react';
import Table from '../../components/common/Table';
import { ExerciseActivityTargetDTO } from '../../services/apiClient';
import { renderBooleanBadge } from '../../utils/displayUtils';
import { Link } from 'lucide-react';
import { convertToUserTime } from '../../utils/dateUtils/convertToUserTime';
import FileIcons from '../common/FileIcons';

interface ExerciseActivityTargetTableProps {
  data: ExerciseActivityTargetDTO[];
  handleEdit: (row: ExerciseActivityTargetDTO) => void;
  handleDelete: (row: ExerciseActivityTargetDTO) => void;
  currentPage: number;
  totalPages: number;
  setCurrentPage: (page: number) => void;
  pageSize: number;
  setPageSize: (size: number) => void;
  isLoading: boolean;
}

const ExerciseActivityTargetTable: React.FC<
  ExerciseActivityTargetTableProps
> = ({
  data,
  handleEdit,
  handleDelete,
  currentPage,
  totalPages,
  setCurrentPage,
  pageSize,
  setPageSize,
  isLoading,
}) => {
  const handleExternalLinkClick = (url: string) => {
    if (!url) return;
    window.open(url, '_blank', 'noopener,noreferrer');
  };

  return (
    <Table
      columns={[
        {
          label: 'Title',
          key: 'title',
        },
        {
          label: 'Position',
          key: 'displayPosition',
        },
        {
          label: 'Target',
          key: 'imageUrl',
          render: (row: ExerciseActivityTargetDTO) => {
            if (row.isUrl && row.imageUrl) {
              return (
                <button
                  onClick={() => handleExternalLinkClick(row.imageUrl!)}
                  className="inline-flex items-center text-primary hover:text-secondary cursor-pointer"
                >
                  <Link className="w-4 h-4" />
                </button>
              );
            }

            return (
              <FileIcons
                files={[
                  {
                    id: row.imageId,
                    label: 'Target Image',
                  },
                ]}
              />
            );
          },
        },
        {
          label: 'Link',
          key: 'isUrl',
          render: (row: ExerciseActivityTargetDTO) =>
            renderBooleanBadge(row.isUrl ?? false, 'Yes', 'No'),
        },
        {
          label: 'Active',
          key: 'isActive',
          render: (row: ExerciseActivityTargetDTO) =>
            renderBooleanBadge(row.isActive ?? false),
        },
        {
          label: 'Added',
          key: 'createdUtc',
          format: (createdUtc: Date) =>
            convertToUserTime(createdUtc?.toString() ?? ''),
        },
      ]}
      data={data}
      actions={{
        onEdit: handleEdit,
        onDelete: handleDelete,
      }}
      currentPage={currentPage}
      totalPages={totalPages}
      onPageChange={setCurrentPage}
      pageSize={pageSize}
      onPageSizeChange={setPageSize}
      isLoading={isLoading}
    />
  );
};

export default ExerciseActivityTargetTable;
